import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import React, {
  Dispatch,
  Fragment,
  ReactNode,
  useEffect,
  useState,
} from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useQuery } from 'react-query';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useNavigate } from 'react-router-dom';
import { useSession, xapi } from '../../hooks/session';
import { schoolListState } from '../../common/atom';
import {
  educationOffices,
  getSchoolClassCount,
  getSchoolGradeCount,
  getSchoolInSchoolGroup,
  schoolGroups,
} from '../../common/helper';
import {
  KEY_SCHOOL_GRADE_SCHOOL_CLASS_LIST,
  KEY_SCHOOL_LIST,
} from '../../common/key';
import {
  getSchoolGradeSchoolClassList,
  getSchoolList,
} from '../../common/api/ApiSchool';

const DEFAULT_WIDTH = '8rem';
interface Props {
  tabValue: number;
  schoolClassInfo: any;
  setSchoolClassInfo: Dispatch<React.SetStateAction<any>>;
  onChangeTab?: (data: number) => void;
}

export const SwClassStatTab: React.FC<Props> = ({
  tabValue,
  schoolClassInfo,
  setSchoolClassInfo,
  onChangeTab,
}) => {
  const navigate = useNavigate();
  const { loginVal } = useSession();
  const [schoolList, setSchoolList] = useState<any[]>([]);
  const [schoolClassList, setSchoolClassList] = useState<any[]>([]);
  const [schools, setSchools] = useState<any[]>([]);
  const [educationOfficeSeq, setEducationOfficeSeq] = useState<number>(
    loginVal.value.user.educationOfficeSeq !== 0
      ? loginVal.value.user.educationOfficeSeq
      : 0
  );
  const [schoolClassSeq, setSchoolClassSeq] = useState<number>(0);
  const [schoolGroupSeq, setSchoolGroupSeq] = useState<number>(1);
  const [schoolSeq, setSchoolSeq] = useState<number>(0);
  const [schoolGrade, setSchoolGrade] = useState<number>(1);
  const [schoolClass, setSchoolClass] = useState<string>('1');
  const [saveTabValue, setSaveTabValue] = useState<number>(tabValue);

  const { refetch: schoolClassRefetch } = useQuery(
    KEY_SCHOOL_GRADE_SCHOOL_CLASS_LIST,
    () => {
      const newData: any = {
        schoolSeq: schoolSeq,
        schoolGrade: schoolGrade,
      };
      if (schoolSeq > 0) return getSchoolGradeSchoolClassList(newData);
      return null;
    },
    {
      onSuccess: res => {
        if (res !== null && res.schoolClassList !== undefined) {
          setSchoolClassList(res.schoolClassList);
        }
      },
      onError: e => {
        console.log(e);
      },
    }
  );

  const reloadSchools = (newSchoolList: any[]) => {
    const newSchools = getSchoolInSchoolGroup(
      newSchoolList,
      schoolGroupSeq,
      educationOfficeSeq
    );
    setSchools(newSchools);
  };

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setSaveTabValue(newValue);

    switch (newValue) {
      case 0:
        navigate('/classstat/userstat');
        break;
      case 1:
        navigate('/classstat/classrank');
        break;
      case 2:
        navigate('/classstat/classstatistics');
        break;
      case 3:
        navigate('/classstat/classlogmanage');
        break;
      default:
        navigate('/classstat');
    }
  };

  useEffect(() => {
    reloadSchools(schoolList);
  }, [schoolGroupSeq, educationOfficeSeq]);

  useEffect(() => {
    schoolClassRefetch();
  }, [schoolSeq, schoolGrade]);

  useEffect(() => {
    const newSchool = schools.filter((s: any) => s.schoolSeq === schoolSeq);
    setSchoolClassInfo((prev: any) => ({
      ...prev,
      educationOfficeSeq: educationOfficeSeq,
      schoolGroupSeq: schoolGroupSeq,
      schoolSeq: schoolSeq,
      schoolGrade: schoolGrade,
      schoolClass: schoolClass,
      schoolName:
        newSchool !== null && newSchool.length > 0
          ? newSchool[0].schoolName
          : '',
    }));
  }, [schoolSeq, schoolClass]);

  useEffect(() => {
    if (schoolClassInfo !== undefined && schoolClassInfo.schoolSeq !== 0) {
      setEducationOfficeSeq(schoolClassInfo.educationOfficeSeq);
      setSchoolGroupSeq(schoolClassInfo.schoolGroupSeq);
      setSchoolSeq(schoolClassInfo.schoolSeq);
      setSchoolGrade(
        schoolClassInfo.schoolGrade !== 0 ? schoolClassInfo.schoolGrade : 1
      );
      setSchoolClass(
        schoolClassInfo.schoolClass !== '0' ? schoolClassInfo.schoolClass : '1'
      );
      schoolClassRefetch();
    }
  }, [schoolClassInfo]);

  useEffect(() => {
    const savedSchoolList = localStorage.getItem(KEY_SCHOOL_LIST);
    if (savedSchoolList) {
      const newSchoolList = JSON.parse(savedSchoolList);
      setSchoolList(newSchoolList);
      reloadSchools(newSchoolList);
    }
  }, []);

  return (
    <Box>
      <Box
        className='flex_between'
        sx={{ mb: '2rem', borderBottom: 1, borderColor: 'divider' }}
      >
        <Tabs value={saveTabValue} onChange={handleChange}>
          <Tab label='학생 관리' sx={{ width: DEFAULT_WIDTH }} />
          <Tab label='랭킹' sx={{ width: DEFAULT_WIDTH }} />
          <Tab label='통계' sx={{ width: DEFAULT_WIDTH }} />
          <Tab label='로그(글) 관리' sx={{ width: DEFAULT_WIDTH }} />
        </Tabs>
        <Box>
          <Box className='flex_start'>
            <TextField
              disabled={
                loginVal.value.user.roleName === '일반' ||
                loginVal.value.user.roleName === '관리자1' ||
                (loginVal.value.user.roleName === '관리자2' &&
                  loginVal.value.user.educationOfficeSeq !== undefined &&
                  loginVal.value.user.educationOfficeSeq !== null)
              }
              sx={{ width: '10rem', mr: '.5rem' }}
              select
              value={educationOfficeSeq}
              onChange={evt => setEducationOfficeSeq(Number(evt.target.value))}
            >
              {educationOffices.map((item: any, idx: number) => (
                <MenuItem key={`educatin_office_${idx}`} value={item.seq}>
                  {item.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              disabled={
                loginVal.value.user.roleName === '일반' ||
                loginVal.value.user.roleName === '관리자1'
              }
              sx={{ width: '10rem', mr: '.5rem' }}
              select
              value={schoolGroupSeq}
              onChange={evt => {
                setSchoolGroupSeq(Number(evt.target.value));
                setSchoolSeq(0);
              }}
            >
              {schoolGroups.map((item: any, idx: number) => (
                <MenuItem key={`school_group_${idx}`} value={item.seq}>
                  {item.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              disabled={
                loginVal.value.user.roleName === '일반' ||
                loginVal.value.user.roleName === '관리자1'
              }
              sx={{ width: '15rem', mr: '.5rem' }}
              select
              value={schoolSeq}
              onChange={evt => setSchoolSeq(Number(evt.target.value))}
            >
              <MenuItem key={`school_${0}`} value={0}>
                선택
              </MenuItem>
              {schools &&
                schools.map((item: any, idx: number) => (
                  <MenuItem key={`school_${idx}`} value={item.schoolSeq}>
                    {item.schoolName}
                  </MenuItem>
                ))}
            </TextField>
            <Box className='flex_start'>
              <TextField
                disabled={loginVal.value.user.roleName === '일반'}
                sx={{ width: '7rem', mr: '.5rem' }}
                select
                value={schoolGrade}
                onChange={evt => setSchoolGrade(Number(evt.target.value))}
              >
                {Array.from(
                  { length: getSchoolGradeCount(schoolGroupSeq) },
                  (_, index) => (
                    <MenuItem
                      key={`school_grade_${index + 1}`}
                      value={index + 1}
                    >
                      {`${index + 1} 학년`}
                    </MenuItem>
                  )
                )}
              </TextField>
              {schoolClassList !== null && schoolClassList.length > 0 ? (
                <TextField
                  disabled={loginVal.value.user.roleName === '일반'}
                  sx={{ width: '7rem' }}
                  select
                  value={schoolClass}
                  onChange={evt => setSchoolClass(evt.target.value)}
                >
                  {schoolClassList.map((sc: string, index: number) => (
                    <MenuItem key={`school_class_${index + 1}`} value={sc}>
                      {`${sc} 반`}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <TextField
                  disabled={loginVal.value.user.roleName === '일반'}
                  sx={{ width: '7rem' }}
                  select
                  value={schoolClass}
                  onChange={evt => setSchoolClass(evt.target.value)}
                >
                  {Array.from({ length: getSchoolClassCount() }, (_, index) => (
                    <MenuItem
                      key={`school_class_${index + 1}`}
                      value={index + 1}
                    >
                      {`${index + 1} 반`}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
